<template>
    <div class="tw-relative" ref="wrap" @mousemove="onMove" @mouseleave="hoverIndex = null">
        <svg :viewBox="`0 0 ${W} ${H}`" preserveAspectRatio="none" class="tw-w-full" :style="{ height: height + 'px' }">
            <defs>
                <linearGradient v-for="s in series" :key="'g' + s.name" :id="gid(s.name)" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" :stop-color="s.color" stop-opacity="0.28" />
                    <stop offset="100%" :stop-color="s.color" stop-opacity="0" />
                </linearGradient>
            </defs>

            <!-- Grid lines -->
            <line v-for="(gy, i) in gridYs" :key="'grid' + i" :x1="padL" :x2="W - padR" :y1="gy" :y2="gy"
                stroke="#e5e7eb" stroke-width="1" vector-effect="non-scaling-stroke" />

            <!-- Areas + lines -->
            <template v-for="s in series" :key="s.name">
                <path :d="areaPath(s.points)" :fill="`url(#${gid(s.name)})`" />
                <path :d="linePath(s.points)" fill="none" :stroke="s.color" stroke-width="2.5"
                    stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke" />
            </template>

            <!-- Hover crosshair + markers -->
            <template v-if="hoverIndex !== null">
                <line :x1="xAt(hoverIndex)" :x2="xAt(hoverIndex)" :y1="padT" :y2="H - padB" stroke="#c7c3d4" stroke-width="1" stroke-dasharray="4 3" vector-effect="non-scaling-stroke" />
                <circle v-for="s in series" :key="'m' + s.name" :cx="xAt(hoverIndex)" :cy="yAt(s.points[hoverIndex])" r="4.5"
                    :fill="s.color" stroke="#fff" stroke-width="2" />
            </template>
        </svg>

        <!-- X labels -->
        <div class="tw-flex tw-justify-between tw-px-1 tw-mt-1">
            <span v-for="(l, i) in labels" :key="'xl' + i" class="tw-text-[10px] tw-text-gray-400">{{ l }}</span>
        </div>

        <!-- Legend -->
        <div class="tw-flex tw-items-center tw-gap-4 tw-mt-2 tw-flex-wrap">
            <span v-for="s in series" :key="'lg' + s.name" class="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-500">
                <span class="tw-w-3 tw-h-1.5 tw-rounded-full" :style="{ background: s.color }"></span>{{ s.name }}
            </span>
        </div>

        <!-- Tooltip -->
        <div v-if="hoverIndex !== null" class="tw-absolute tw-pointer-events-none tw-z-10 tw-bg-gray-900 tw-text-white tw-text-xs tw-rounded-lg tw-px-3 tw-py-2 tw-shadow-lg"
            :style="tooltipStyle">
            <p class="tw-font-semibold tw-mb-1">{{ labels[hoverIndex] }}</p>
            <p v-for="s in series" :key="'tt' + s.name" class="tw-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap">
                <span class="tw-w-2 tw-h-2 tw-rounded-full" :style="{ background: s.color }"></span>
                {{ s.name }}: <span class="tw-font-semibold">{{ format ? format(s.points[hoverIndex]) : s.points[hoverIndex] }}</span>
            </p>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, defineProps } from 'vue';

const props = defineProps({
    series: { type: Array, default: () => [] }, // [{ name, color, points: number[] }]
    labels: { type: Array, default: () => [] },
    height: { type: Number, default: 240 },
    format: { type: Function, default: null },
});

const W = 600, H = 240;
const padL = 8, padR = 8, padT = 14, padB = 8;

const wrap = ref(null);
const hoverIndex = ref(null);

const maxVal = computed(() => {
    const all = props.series.flatMap(s => s.points || []);
    return Math.max(1, ...all);
});

const n = computed(() => props.labels.length || 1);

const xAt = (i) => padL + (n.value <= 1 ? 0 : (i / (n.value - 1)) * (W - padL - padR));
const yAt = (v) => {
    const usable = H - padT - padB;
    return padT + usable - (Math.max(0, v) / maxVal.value) * usable;
};

const gridYs = computed(() => [0, 0.25, 0.5, 0.75, 1].map(t => padT + (H - padT - padB) * t));

const linePath = (pts) => pts.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(v).toFixed(1)}`).join(' ');
const areaPath = (pts) => {
    if (!pts.length) return '';
    const line = pts.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(v).toFixed(1)}`).join(' ');
    return `${line} L ${xAt(pts.length - 1).toFixed(1)} ${(H - padB).toFixed(1)} L ${xAt(0).toFixed(1)} ${(H - padB).toFixed(1)} Z`;
};

const gid = (name) => 'grad-' + String(name).replace(/\W/g, '');

const onMove = (e) => {
    const el = wrap.value;
    if (!el || n.value <= 1) return;
    const rect = el.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    hoverIndex.value = Math.round(frac * (n.value - 1));
};

const tooltipStyle = computed(() => {
    const frac = n.value <= 1 ? 0 : hoverIndex.value / (n.value - 1);
    const left = frac * 100;
    return {
        left: `${left}%`,
        top: '0px',
        transform: `translateX(${left > 70 ? '-100%' : left < 30 ? '0' : '-50%'})`,
    };
});
</script>
